import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, switchMap } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getPage,
  load404Page,
  loadPage,
  loadPageFail,
  loadPageSuccess,
  setActivePage,
} from '../actions/cms.actions';
import { CmsGatewayService } from '../gateway/cms.gateway';
import { Store } from '@ngrx/store';
import { BoelsState } from '../../reducers.index';
import { selectPages } from '../selectors/cms.selectors';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { PageType } from '@boels/models';

@Injectable({
  providedIn: 'any',
})
export class CmsEffects {
  private actions$ = inject(Actions);
  private cmsGatewayService = inject(CmsGatewayService);
  private store = inject<Store<BoelsState>>(Store);
  private cultureFacade = inject(CultureFacade);

  // set active Page if page is already in state, otherwise trigger loadPage action
  getPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPage),
      withLatestFrom(
        this.store.select(selectPages),
        this.cultureFacade.getActiveCulture(),
      ),
      map(([action, pageDictionary, culture]) => {
        if (pageDictionary[culture]?.[action.label]) {
          return setActivePage({
            page: pageDictionary[culture]?.[action.label],
          });
        }
        return loadPage({ label: action.label, pageType: action.pageType });
      }),
    ),
  );

  loadPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPage),
      exhaustMap((action) => {
        return this.cmsGatewayService
          .loadPage(action.label, action.pageType)
          .pipe(
            withLatestFrom(this.cultureFacade.getActiveCulture()),
            map(([page, culture]) => {
              return loadPageSuccess({ culture, page, label: action.label });
            }),
            catchError((errorMessage) =>
              of(
                loadPageFail({
                  errorMessage,
                  label: action.label,
                  previousLabel: action.label?.replace(/^\/|\/$/g, ''),
                }),
              ),
            ),
            take(1),
          );
      }),
    ),
  );

  get404Page$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPageFail),
      withLatestFrom(
        this.store.select(selectPages),
        this.cultureFacade.getActiveCulture(),
      ),
      map(([action, pageDictionary, culture]) => {
        if (pageDictionary[culture]?.[action.label]) {
          return setActivePage({
            page: pageDictionary[culture]?.[action.label],
          });
        }
        return load404Page({
          label: action.label,
          pageType: PageType.CONTENT_PAGE,
        });
      }),
    ),
  );

  load404Page$ = createEffect(() =>
    this.actions$.pipe(
      ofType(load404Page),
      switchMap((action) => {
        return this.cmsGatewayService
          .loadPage('/not-found', PageType.CONTENT_PAGE)
          .pipe(
            withLatestFrom(this.cultureFacade.getActiveCulture()),
            map(([page, culture]) => {
              return loadPageSuccess({ culture, page, label: action.label });
            }),
          );
      }),
    ),
  );
}
